// frontend/src/pages/admin/Dashboard.tsx
import React, { useEffect, useState } from 'react';
import { Users, Package, Calendar, TrendingUp, Activity } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../../services/api';

interface GlobalStats {
  total_users: number;
  active_users: number;
  total_products: number;
  active_promotions: number;
  users_by_role: {
    agent: number;
    formateur: number;
    admin: number;
  };
  recent_registrations: number;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<GlobalStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/stats/global');
      setStats(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!stats) return null;

  const usersByRoleData = [
    { name: 'Agents', value: stats.users_by_role.agent },
    { name: 'Formateurs', value: stats.users_by_role.formateur },
    { name: 'Admins', value: stats.users_by_role.admin }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Tableau de bord administrateur</h1>
        <p className="mt-1 text-gray-600">Vue d'ensemble de la plateforme de formation</p>
      </div>

      {/* Cartes de statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Utilisateurs totaux</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_users}</p>
              <p className="text-xs text-green-600 mt-1">
                +{stats.recent_registrations} cette semaine
              </p>
            </div>
            <Users className="h-8 w-8 text-primary" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Utilisateurs actifs</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active_users}</p>
              <p className="text-xs text-gray-500 mt-1">
                {Math.round((stats.active_users / stats.total_users) * 100)}% du total
              </p>
            </div>
            <Activity className="h-8 w-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Produits</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_products}</p>
            </div>
            <Package className="h-8 w-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Promotions actives</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active_promotions}</p>
            </div>
            <Calendar className="h-8 w-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Graphique des utilisateurs par rôle */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Répartition des utilisateurs par rôle</h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={usersByRoleData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#004E89" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Actions rapides */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <a
          href="/admin/products"
          className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">Gérer les produits</h3>
              <p className="text-sm text-gray-600 mt-1">
                Ajouter ou modifier des formations
              </p>
            </div>
            <Package className="h-8 w-8 text-gray-400" />
          </div>
        </a>

        <a
          href="/admin/promotions"
          className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">Gérer les promotions</h3>
              <p className="text-sm text-gray-600 mt-1">
                Créer de nouvelles sessions
              </p>
            </div>
            <Calendar className="h-8 w-8 text-gray-400" />
          </div>
        </a>

        <a
          href="/admin/users"
          className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">Gérer les utilisateurs</h3>
              <p className="text-sm text-gray-600 mt-1">
                Administrer les comptes
              </p>
            </div>
            <Users className="h-8 w-8 text-gray-400" />
          </div>
        </a>
      </div>
    </div>
  );
}